"use client";

import { errorLoggingService } from "@/lib/error-logging-service";

const SEARCH_HISTORY_KEY = "ezrecipebox_search_history";
const MAX_HISTORY_ITEMS = 10;

export interface SearchHistoryItem {
  query: string;
  filters?: Record<string, unknown> | null;
  timestamp: number;
}

/**
 * Get search history from localStorage
 */
export function getSearchHistory(): SearchHistoryItem[] {
  if (typeof window === "undefined") {
    return [];
  }

  let historyJson: string | null = null;
  try {
    historyJson = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (!historyJson) {
      return [];
    }

    const history = JSON.parse(historyJson) as SearchHistoryItem[];
    return history.sort((a, b) => b.timestamp - a.timestamp).slice(0, MAX_HISTORY_ITEMS);
  } catch (error) {
    errorLoggingService.logError({
      context: "getSearchHistory",
      error: error as Error,
      additionalData: { hasHistoryJson: !!historyJson },
    });
    return [];
  }
}

/**
 * Add a search to history
 */
export function addToSearchHistory(
  query: string,
  filters?: Record<string, unknown> | null,
): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const history = getSearchHistory();
    
    // Remove duplicate entries (same query and filters)
    const filtered = history.filter(
      (item) => item.query !== query || JSON.stringify(item.filters) !== JSON.stringify(filters),
    );

    // Add new entry at the beginning
    const newItem: SearchHistoryItem = {
      query,
      filters,
      timestamp: Date.now(),
    };

    const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
  } catch (error) {
    errorLoggingService.logError({
      context: "addToSearchHistory",
      error: error as Error,
      additionalData: { query, hasFilters: !!filters },
    });
  }
}

/**
 * Clear search history
 */
export function clearSearchHistory(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch (error) {
    errorLoggingService.logError({
      context: "clearSearchHistory",
      error: error as Error,
    });
  }
}

/**
 * Remove a specific item from search history
 */
export function removeFromSearchHistory(timestamp: number): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const history = getSearchHistory();
    const filtered = history.filter((item) => item.timestamp !== timestamp);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(filtered));
  } catch (error) {
    errorLoggingService.logError({
      context: "removeFromSearchHistory",
      error: error as Error,
      additionalData: { timestamp },
    });
  }
}

